import React, { Component } from "react";
import { colors } from "./theme";

// Concentric donut used for the overall result.
//
// The outer ring is the current year, the inner ring the previous one (omitted
// when there is no previous questionnaire). An optional threshold tick marks the
// score needed to pass.
const describeArc = (cx, cy, radius, percentage) => {
  const value = Math.max(0, Math.min(100, percentage || 0));
  // A full circle cannot be drawn with a single arc.
  if (value >= 100) {
    return [
      `M ${cx} ${cy - radius}`,
      `A ${radius} ${radius} 0 1 1 ${cx - 0.01} ${cy - radius}`,
    ].join(" ");
  }

  const angle = (value / 100) * 2 * Math.PI - Math.PI / 2;
  const x = cx + radius * Math.cos(angle);
  const y = cy + radius * Math.sin(angle);

  return [
    `M ${cx} ${cy - radius}`,
    `A ${radius} ${radius} 0 ${value > 50 ? 1 : 0} 1 ${x} ${y}`,
  ].join(" ");
};

export default class V2Donut extends Component {
  render() {
    const {
      value,
      previousValue,
      threshold,
      size = 320,
      // Ring thickness, matching the `stroke` prop of V1's ReportCircleChart.
      stroke = 40,
      label,
      caption,
      legend,
      currentLabel,
      previousLabel,
    } = this.props;

    const cx = size / 2;
    const cy = size / 2;
    // Same geometry as V1: the previous-year ring is half as thick and sits
    // directly inside the current one, so the two bands touch with no gap.
    const outerRadius = size / 2 - stroke;
    const strokeWidth = stroke;
    const innerRadius = outerRadius - stroke / 4;
    const innerStrokeWidth = stroke / 2;

    const thresholdAngle =
      threshold !== undefined
        ? (threshold / 100) * 2 * Math.PI - Math.PI / 2
        : null;

    return (
      <div className="report-v2__donut">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
        >
          <circle
            cx={cx}
            cy={cy}
            r={outerRadius}
            fill="none"
            stroke={colors.track}
            strokeWidth={strokeWidth}
          />
          <path
            d={describeArc(cx, cy, outerRadius, value)}
            fill="none"
            stroke={colors.primary}
            strokeWidth={strokeWidth}
          />

          {previousValue !== null && previousValue !== undefined && (
            <>
              <circle
                cx={cx}
                cy={cy}
                r={innerRadius}
                fill="none"
                stroke={colors.track}
                strokeWidth={innerStrokeWidth}
              />
              {/* tertiary, not secondary: the bar charts beside this donut use
                  primary for the current year, tertiary for the previous one
                  and secondary for the Employer Partner average, so a navy
                  inner ring read as the benchmark rather than last year. */}
              <path
                d={describeArc(cx, cy, innerRadius, previousValue)}
                fill="none"
                stroke={colors.tertiary}
                strokeWidth={innerStrokeWidth}
              />
            </>
          )}

          {thresholdAngle !== null && (
            <line
              x1={
                cx + (innerRadius - innerStrokeWidth) * Math.cos(thresholdAngle)
              }
              y1={
                cy + (innerRadius - innerStrokeWidth) * Math.sin(thresholdAngle)
              }
              x2={
                cx + (outerRadius + strokeWidth / 2) * Math.cos(thresholdAngle)
              }
              y2={
                cy + (outerRadius + strokeWidth / 2) * Math.sin(thresholdAngle)
              }
              stroke={colors.threshold}
              strokeWidth={2}
            />
          )}

          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            className="report-v2__donut-value"
            style={{ fontSize: Math.round(size * 0.19) }}
          >
            {label !== undefined ? label : `${Math.round(value)}%`}
          </text>
        </svg>

        {caption && <p className="report-v2__donut-caption">{caption}</p>}

        {legend && (
          <ul className="report-v2__legend">
            <li>
              <span style={{ background: colors.primary }} />
              {currentLabel}
            </li>
            {previousValue !== null && previousValue !== undefined && (
              <li>
                <span style={{ background: colors.tertiary }} />
                {previousLabel}
              </li>
            )}
          </ul>
        )}
      </div>
    );
  }
}
